import { useRef, useEffect } from "react";

import { cn } from "@/lib/utils";
import { TextareaAuto } from "@/components/ui/textarea-auto";
import { Button } from "@/components/ui/button";
import useKeyboardDetection from "@/hooks/useKeyboardDetection";

import useChatStore from "../stores/useChatStore";

function FooterContent({ className, isPopupContent = false, inputClassName, isMobile = false, disabled = false }) {
  const footerRef = useRef(null);
  const keyboardState = useKeyboardDetection();

  const question = useChatStore((store) => store.question);
  const isAnswering = useChatStore((store) => store.isAnswering);
  const setQuestion = useChatStore((store) => store.setQuestion);
  const conversation = useChatStore((store) => store.conversation);
  const handleAskQuestionStream = useChatStore((store) => store.handleAskQuestionStream);
  const setFooterHeight = useChatStore((store) => store.setFooterHeight);

  useEffect(() => {
    if (footerRef.current) {
      const height = footerRef.current.offsetHeight;
      setFooterHeight(height);
    }
  }, [question, setFooterHeight]);

  const onSubmit = () => {
    if (!question.trim() || isAnswering) return;

    handleAskQuestionStream(); // Use streaming instead of regular API

    // Delay việc clear input để đảm bảo IME composition đã hoàn tất
    setTimeout(() => {
      setQuestion("");
    }, 10);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleTextareaHeightChange = (textareaHeight) => {
    const maxHeight = isMobile ? 120 : 160;
    const actualHeight = Math.min(textareaHeight, maxHeight);
    const totalHeight = actualHeight + (isMobile ? 20 : 40);
    setFooterHeight(totalHeight);
  };

  return (
    <div
      className={cn(
        "w-full gap-2",
        className,
        // Khi keyboard hiện trên mobile, thêm class để adjust position
        isMobile && keyboardState.isVisible && "transition-all duration-200 ease-out"
      )}
      ref={footerRef}
      style={
        isMobile && keyboardState.isVisible
          ? {
              // Đảm bảo input luôn visible khi keyboard hiện
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }
          : {}
      }
    >
      <div className="relative">
        <TextareaAuto
          value={question}
          placeholder="Tôi có thể giúp gì cho bạn?"
          className={cn(
            "flex-1 focus-visible:border-none placeholder:text-disabled",
            "max-h-[160px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
            "hover:scrollbar-thumb-gray-400",
            isMobile && "max-h-[120px]",
            inputClassName
          )}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          onHeightChange={handleTextareaHeightChange}
          disabled={disabled}
        />
        <Button
          size={isPopupContent ? "sm" : "md"}
          className={cn("absolute right-0 top-1/2 -translate-y-1/2 ", isPopupContent ? "mr-2 h-7 w-7" : "mr-3 h-9 w-9")}
          disabled={!question.trim() || isAnswering || disabled}
          onClick={onSubmit}
        >
          →
        </Button>
      </div>
      <div
        className={cn("text-xs text-gray-500 text-center mt-2", {
          visible: conversation.length > 0 && !isPopupContent && !keyboardState.isVisible,
          invisible: conversation.length === 0 || isPopupContent || keyboardState.isVisible,
        })}
      >
        Trợ lý AI có thể mắc lỗi. Hãy kiểm tra các thông tin quan trọng.
      </div>
    </div>
  );
}

export default FooterContent;
